/**
 * GroupMatrixTab 價格群組矩陣 (S9.1) — Group A–D × Source × Level markup grid.
 *
 * - Columns = Group A–D with the band range in the header; rows = Source
 *   CH/TW; each cell shows per-level factor chips ("PL09 +80%"). The plan
 *   sketch says rows Source×Category, but the FROZEN S1.5 `MarkupRule` schema
 *   carries no category dimension — rows are Source until one lands
 *   (deviation recorded on the slice).
 * - The matrix CURRENCY is a USER SELECTOR (W9b 0708-J audit:「使用者選擇」)
 *   — bands filter by the chosen currency, never hardcoded currency tabs.
 * - Cell click → zodResolver edit modal: one markup % per active level
 *   (blank = no rule) + unlimited "add price level" rows (levels are
 *   user-defined rows, never the NetSuite PL01–17 hardcode).
 * - Mutations are store-first (`usePricingMutations`) — the same pure
 *   reducers vitest drives to prove a markup edit changes the next resolved
 *   SO-line price.
 *
 * S5.2 (plan §S5.2) — schedule/group management INSIDE this existing setup
 * tab (no new routes, Appendix D #14): two admin cards below the matrix
 * manage 定價群組 (Discribe-20) and 數量定價排程 (DD!116) via quick-create
 * rows reusing the `TaxonomyQuickCreateRow` 3-field pattern and inheriting
 * its FULL state matrix — idle / validating / code-taken / in-flight /
 * failure — encoded here as the PURE exported `quickCreateTransition` FSM
 * (vitest-pinned; the component only dispatches events). Writes stay
 * store-first (`pricingStore` reducers only); the schedule save is
 * price-affecting so it goes through `ReasonRequiredModal` →
 * `saveQuantitySchedule` (REASON_REQUIRED → PRICE_OVERRIDE audit row), while
 * group CREATION is not price-affecting until a card references it
 * (`addPricingGroup`, no reason gate — store doctrine). Both cards declare
 * explicit empty and error states (ux-lens).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  Modal,
  InputNumber,
  Input,
  Popover,
  Row,
  Select,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  EditOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { actionColumn } from '@/components/common/ActionDropdown';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useCurrencies } from '@/hooks/useCurrencies';
import {
  PRICE_SOURCES,
  markupRuleIndex,
  pricingAsOf,
  usePricingDataset,
  usePricingMutations,
} from '@/hooks/usePricing';
import {
  DEFAULT_BAND_SET_SCOPE,
  bandSetScopeOf,
  isBandActive,
  planBandCutMoves,
} from '@/lib/pricing';
import type { BandCutMove, BandSetViolation, BandSetViolationCode } from '@/lib/pricing';
import { useTaxonomyTree } from '@/hooks/useTaxonomy';
import TaxonomyCascader from '@/components/common/TaxonomyCascader';
import { buildQuickCreatePayload, quickCreateFieldsSchema } from './quickCreatePayload';
import type { QuickCreateFields } from './quickCreatePayload';
import {
  bandSetCurrencies,
  bandsInSet,
  isCellUnset,
  markupCellEdits,
  markupCellKey,
  matrixColumnBands,
  matrixCurrencyFor,
} from './matrixScope';
import GroupDiscountCardModal from './GroupDiscountCardModal';
import ScheduleEditorModal from './ScheduleEditorModal';
import type { ScheduleFormValues } from './ScheduleEditorModal';
import { PricingGuardError } from '@/stores/pricingStore';
import type {
  MarkupRuleEdit,
  PriceGroupBandCreate,
  PriceGroupBandPatch,
  PricingGroupPatch,
} from '@/stores/pricingStore';
import { useElevation } from '@/styles';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type {
  CustomerGroupDiscount,
  PriceGroupBand,
  PriceGroupCode,
  PriceLevel,
  PriceSource,
  PricingGroup,
  QuantityPricingSchedule,
} from '@/types/api/pricing';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Edit-modal form contract (zodResolver — no Controller rules)
// ---------------------------------------------------------------------------

const markupPctSchema = z
  .number({ invalid_type_error: 'pricing.matrix.modal.validation.markupInvalid' })
  .min(-100, 'pricing.matrix.modal.validation.markupInvalid')
  .max(1000, 'pricing.matrix.modal.validation.markupInvalid');

const cellFormSchema = z.object({
  /** One entry per EXISTING active level; null markup = no rule for the cell. */
  levelEntries: z.array(
    z.object({
      priceLevelId: z.string(),
      code: z.string(),
      markupPct: markupPctSchema.nullable(),
    })
  ),
  /** Unlimited user-added levels (created matrix-wide on save). */
  newLevels: z.array(
    z.object({
      code: z.string().trim().min(1, 'pricing.matrix.modal.validation.codeRequired'),
      nameEn: z.string().trim().min(1, 'pricing.matrix.modal.validation.nameEnRequired'),
      nameZh: z.string().trim().min(1, 'pricing.matrix.modal.validation.nameZhRequired'),
      markupPct: markupPctSchema,
    })
  ),
});

type CellFormData = z.infer<typeof cellFormSchema>;

/**
 * S2.1 group-edit contract — names / policy rate / taxonomy link. The CODE is
 * deliberately not a form field: it is immutable after creation (W6 read-only
 * presentation in the modal), mirrored at type level by `PricingGroupPatch`.
 */
const groupEditSchema = z.object({
  nameZh: z.string().trim().min(1, 'pricing.quickCreate.validation.nameZhRequired'),
  nameEn: z.string().trim().min(1, 'pricing.quickCreate.validation.nameEnRequired'),
  defaultDiscountPct: z
    .number({ invalid_type_error: 'pricing.groupsAdmin.editModal.validation.discountRange' })
    .min(0, 'pricing.groupsAdmin.editModal.validation.discountRange')
    .max(100, 'pricing.groupsAdmin.editModal.validation.discountRange')
    .nullable(),
  categoryId: z.string().nullable(),
});

type GroupEditFormData = z.infer<typeof groupEditSchema>;

/** Pending reason-gated group action (edit save XOR activate toggle). */
type PendingGroupAction =
  | { kind: 'update'; groupId: string; patch: PricingGroupPatch }
  | { kind: 'toggle'; groupId: string; isActive: boolean };

/**
 * P2 band-editor contract. `scope` and `currency` are form fields on CREATE
 * only — on edit they render READ-ONLY (the immutable-code presentation
 * above), and `PriceGroupBandPatch` carries no member for either, so the
 * modal cannot express a cross-set or cross-currency move even by accident.
 *
 * The half-open / gapless / non-overlapping RULE IS NOT RESTATED HERE. It
 * lives once, in the pure `bandSetInvariants` module the store reducers
 * enforce with; duplicating it in a zod refinement would put a second copy in
 * a `.tsx` that no node-environment guard can reach, which is exactly the
 * shape `erp-web-gate-reality` §2 warns about. This schema checks only what a
 * single field can answer on its own.
 */
const bandEditSchema = z.object({
  scope: z.string().min(1),
  currency: z.string().trim().min(1, 'pricing.bandsAdmin.editModal.validation.currencyRequired'),
  group: z.enum(['A', 'B', 'C', 'D']),
  minInclusive: z
    .number({ invalid_type_error: 'pricing.bandsAdmin.editModal.validation.minInvalid' })
    .min(0, 'pricing.bandsAdmin.editModal.validation.minInvalid'),
  /** `null` = the open-ended top band (blank input), never a fabricated ceiling. */
  maxExclusive: z
    .number({ invalid_type_error: 'pricing.bandsAdmin.editModal.validation.maxInvalid' })
    .nullable(),
});

type BandEditFormData = z.infer<typeof bandEditSchema>;

/** Pending reason-gated band action (create / edit save XOR activate toggle). */
type PendingBandAction =
  | { kind: 'create'; create: PriceGroupBandCreate }
  | { kind: 'update'; bandId: string; patch: PriceGroupBandPatch }
  /**
   * P3 — the edit moves a SHARED CUT, so the touching neighbour's matching
   * bound moves with it. `moves` is the plan the editor already showed the
   * operator; the store recomputes it from the same pure function, so the
   * preview and the write cannot drift.
   */
  | { kind: 'moveCut'; bandId: string; patch: PriceGroupBandPatch; moves: BandCutMove[] }
  | { kind: 'toggle'; bandId: string; isActive: boolean };

/**
 * Violation code → the i18n key that NAMES the conflicting boundary. A static
 * record rather than a template-literal key: `scripts/audit-translations.cjs`
 * cannot resolve an interpolated key, and a key it cannot resolve is a key it
 * reports as invalid.
 */
/**
 * The A–D vocabulary, mirrored from the `PriceGroupCode` union. Written as a
 * `Record<PriceGroupCode, true>` on purpose: widening the union without
 * widening this list is a TYPE ERROR, so the picker can never silently fall
 * behind the type. Unlike price LEVELS (user-defined rows, never a hardcoded
 * enum) the band letters are a closed coarse layer — RULING R-AD keeps A–D as
 * exactly that, so a fixed list here is the honest shape.
 */
const PRICE_GROUP_CODE_SET: Record<PriceGroupCode, true> = { A: true, B: true, C: true, D: true };
const PRICE_GROUP_CODES = Object.keys(PRICE_GROUP_CODE_SET) as PriceGroupCode[];

/**
 * P3 — which NEIGHBOUR bound moves, as a static record for the same reason
 * `BAND_VIOLATION_KEY` is one: `scripts/audit-translations.cjs` cannot resolve
 * an interpolated key, and a key it cannot resolve it reports as invalid.
 */
const BAND_CUT_BOUND_KEY: Record<BandCutMove['field'], string> = {
  minInclusive: 'pricing:pricing.bandsAdmin.cutMove.lowerBound',
  maxExclusive: 'pricing:pricing.bandsAdmin.cutMove.upperBound',
};

const BAND_VIOLATION_KEY: Record<BandSetViolationCode, string> = {
  BAND_GAP: 'pricing:pricing.bandsAdmin.violation.gap',
  BAND_OVERLAP: 'pricing:pricing.bandsAdmin.violation.overlap',
  BAND_RANGE_INVALID: 'pricing:pricing.bandsAdmin.violation.rangeInvalid',
  BAND_OPEN_END_NOT_TOP: 'pricing:pricing.bandsAdmin.violation.openEndNotTop',
};

interface EditingCell {
  band: PriceGroupBand;
  source: PriceSource;
}

interface MatrixRow {
  source: PriceSource;
}

// ---------------------------------------------------------------------------
// S5.2 — quick-create state matrix (PURE, exported for vitest)
//
// The full TaxonomyQuickCreateRow state matrix — idle / validating /
// code-taken / in-flight / failure — as an explicit transition function so
// the node-env suite can prove every state reachable and every guard
// (no-double-file, taken-blocks-submit, retry-after-failure) without jsdom.
// `code_available` is the success verdict of a code check (the green tick),
// sitting alongside the five plan-named states.
// ---------------------------------------------------------------------------

export type QuickCreateStatus =
  | 'idle'
  | 'validating'
  | 'code_available'
  | 'code_taken'
  | 'in_flight'
  | 'failure';

export type QuickCreateEvent =
  | { type: 'CODE_CHECK_START' }
  | { type: 'CODE_CHECK_RESULT'; taken: boolean }
  | { type: 'CODE_EDITED' }
  | { type: 'SUBMIT' }
  | { type: 'SUBMIT_OK' }
  | { type: 'SUBMIT_FAIL' }
  | { type: 'RESET' };

/**
 * One transition of the quick-create matrix. Guards encoded structurally:
 * - an in-flight save ignores everything except its own outcome (no
 *   double-file, no verdict churn mid-save);
 * - `code_taken` blocks SUBMIT (stays put — the component refocuses code);
 * - `failure` keeps values (component-side) and allows retry via SUBMIT;
 * - a CODE_CHECK_RESULT only lands while `validating` (stale checks drop).
 */
// eslint-disable-next-line react-refresh/only-export-components -- vitest-pinned pure FSM (TableContainer renderDash precedent)
export function quickCreateTransition(
  status: QuickCreateStatus,
  event: QuickCreateEvent
): QuickCreateStatus {
  if (status === 'in_flight' && event.type !== 'SUBMIT_OK' && event.type !== 'SUBMIT_FAIL') {
    return 'in_flight';
  }
  switch (event.type) {
    case 'CODE_CHECK_START':
      return 'validating';
    case 'CODE_CHECK_RESULT':
      if (status !== 'validating') return status;
      return event.taken ? 'code_taken' : 'code_available';
    case 'CODE_EDITED':
      return 'idle';
    case 'SUBMIT':
      return status === 'code_taken' ? 'code_taken' : 'in_flight';
    case 'SUBMIT_OK':
      return status === 'in_flight' ? 'idle' : status;
    case 'SUBMIT_FAIL':
      return status === 'in_flight' ? 'failure' : status;
    case 'RESET':
      return 'idle';
  }
}

/** Case-insensitive availability check against the codes already in the dataset. */
// eslint-disable-next-line react-refresh/only-export-components -- vitest-pinned pure helper
export function isPricingCodeAvailable(code: string, takenCodes: readonly string[]): boolean {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return false;
  return !takenCodes.some((taken) => taken.trim().toUpperCase() === normalized);
}

/**
 * Suggest-and-confirm code seed (the taxonomy `suggestCategoryCode` shape):
 * `<PREFIX>-<EN-STEM>`, de-duplicated with a numeric suffix. Deterministic
 * for a given taken-set (exported for vitest).
 */
// eslint-disable-next-line react-refresh/only-export-components -- vitest-pinned pure helper
export function suggestPricingCode(
  prefix: string,
  nameEn: string,
  takenCodes: readonly string[]
): string {
  const stem = nameEn
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 12)
    .replace(/-+$/g, '');
  const base = stem ? `${prefix}-${stem}` : prefix;
  if (isPricingCodeAvailable(base, takenCodes)) return base;
  for (let suffix = 2; suffix < 100; suffix += 1) {
    const candidate = `${base}-${suffix}`;
    if (isPricingCodeAvailable(candidate, takenCodes)) return candidate;
  }
  return `${base}-${Date.now()}`;
}

/** 3-field quick-create contract (nameZh → nameEn → code; no wizard). */

interface ReasonCopy {
  title: string;
  description: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  requiredMessage: string;
  submitText: string;
}

interface PricingQuickCreateFormProps {
  /** Localized card-scoped title shown at the top of the popover. */
  title: string;
  /** Localized one-liner under the title (what the created row does). */
  hint: string;
  /** Code suggestion prefix (PG for groups, QPS for schedules). */
  codePrefix: string;
  /**
   * Render the taxonomy autocomplete above the name fields. Picking a node
   * prefills 中文/英文 names (and the code SUGGESTION follows from the EN
   * name, as it already does when typed by hand).
   */
  categoryPicker?: boolean;
  /** Resolves the picked node for the name prefill (host owns the taxonomy read). */
  categoryLookup?: (categoryId: string) => { nameEn: string; nameZh: string } | undefined;
  /** Codes already present in the dataset — the availability check basis. */
  takenCodes: readonly string[];
  /**
   * Localized ReasonRequiredModal copy — present ⇔ the save is
   * price-affecting (schedule). Absent ⇒ direct store save (group create).
   */
  reasonCopy?: ReasonCopy;
  /**
   * Store-first write (pricingStore reducers only). May throw
   * (PricingGuardError or anything) — a throw lands the FAILURE state with
   * values kept and retry allowed.
   */
  performSave: (values: QuickCreateFields, reason: string | null) => void;
  /** Fired after a successful save (host closes the popover + toasts). */
  onSaved: (nameZh: string) => void;
  /** Dismiss without filing (Cancel / Esc). */
  onCancel: () => void;
}

/**
 * The shared 3-field quick-create row (TaxonomyQuickCreateRow pattern):
 * 名稱（中文）(autofocus) → 名稱（英文）→ 代碼 with suggest-and-confirm,
 * on-blur availability check, and the full exported state matrix. Rendered
 * as the CONTENT of an anchored Popover whose host applies
 * `useElevation().popover`; inner fields round on `radiusTokens.card`.
 */
const PricingQuickCreateForm = ({
  title,
  hint,
  codePrefix,
  categoryPicker,
  categoryLookup,
  takenCodes,
  reasonCopy,
  performSave,
  onSaved,
  onCancel,
}: PricingQuickCreateFormProps) => {
  const { t } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();

  const [status, setStatus] = useState<QuickCreateStatus>('idle');
  const dispatch = useCallback((event: QuickCreateEvent) => {
    setStatus((current) => quickCreateTransition(current, event));
  }, []);

  const [codeEdited, setCodeEdited] = useState(false);
  const [liveMessage, setLiveMessage] = useState('');
  const [reasonOpen, setReasonOpen] = useState(false);
  const pendingValuesRef = useRef<QuickCreateFields | null>(null);

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setFocus,
    clearErrors,
    formState: { errors, touchedFields },
  } = useForm<QuickCreateFields>({
    resolver: zodResolver(quickCreateFieldsSchema),
    defaultValues: { nameZh: '', nameEn: '', code: '', categoryId: null },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Autofocus 中文 (primary field). rAF so focus lands inside the popover portal.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFocus('nameZh'));
    return () => cancelAnimationFrame(raf);
  }, [setFocus]);

  const codeValue = useWatch({ control, name: 'code' });

  const inputStyle = { ...formStyles.inputStyle, borderRadius: radiusTokens.card };
  const requiredMark = (
    <span style={{ color: token.colorError, marginRight: token.marginXXS }}>*</span>
  );

  // Suggest-and-confirm: seed the code from the EN name until the user edits it.
  const maybeSuggestCode = useCallback(() => {
    if (codeEdited) return;
    const nameEn = getValues('nameEn').trim();
    if (!nameEn) return;
    setValue('code', suggestPricingCode(codePrefix, nameEn, takenCodes), {
      shouldValidate: false,
    });
    dispatch({ type: 'CODE_EDITED' }); // fresh code ⇒ verdict cleared
  }, [codeEdited, codePrefix, dispatch, getValues, setValue, takenCodes]);

  const handleCodeUserEdit = useCallback(() => {
    if (!codeEdited) setCodeEdited(true);
    clearErrors('code');
    dispatch({ type: 'CODE_EDITED' });
  }, [clearErrors, codeEdited, dispatch]);

  const handleCodeBlur = useCallback(() => {
    const value = getValues('code').trim();
    if (!value) return;
    dispatch({ type: 'CODE_CHECK_START' });
    dispatch({ type: 'CODE_CHECK_RESULT', taken: !isPricingCodeAvailable(value, takenCodes) });
  }, [dispatch, getValues, takenCodes]);

  const runSave = async (values: QuickCreateFields, reason: string | null) => {
    dispatch({ type: 'SUBMIT' });
    try {
      // Yield a tick so the in-flight state paints before the store write.
      await Promise.resolve();
      performSave(buildQuickCreatePayload(values), reason);
      dispatch({ type: 'SUBMIT_OK' });
      setLiveMessage(t('pricing:pricing.quickCreate.added', { name: values.nameZh.trim() }));
      onSaved(values.nameZh.trim());
    } catch {
      // FAILURE: localized inline error + aria-live, values kept, retry allowed.
      dispatch({ type: 'SUBMIT_FAIL' });
      setLiveMessage(t('common:messages.createFailed'));
    }
  };

  const onValid = (values: QuickCreateFields) => {
    if (status === 'in_flight' || status === 'code_taken') return; // FSM guard mirror
    // Submit-time availability backstop (the taxonomy 409 analogue).
    if (!isPricingCodeAvailable(values.code, takenCodes)) {
      dispatch({ type: 'CODE_CHECK_START' });
      dispatch({ type: 'CODE_CHECK_RESULT', taken: true });
      setLiveMessage(t('pricing:pricing.quickCreate.codeTaken'));
      setFocus('code');
      return;
    }
    if (reasonCopy) {
      // Price-affecting save — the ReasonRequiredModal is the ONLY entry.
      pendingValuesRef.current = values;
      setReasonOpen(true);
      return;
    }
    void runSave(values, null);
  };

  const fieldError = (name: keyof QuickCreateFields) =>
    errors[name] && (touchedFields[name] || errors[name]?.type === 'server') ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  const saving = status === 'in_flight';

  return (
    <form
      data-status={status}
      onSubmit={handleSubmit(onValid)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
      style={{
        width: 'min(300px, calc(100vw - 48px))',
        padding: token.paddingMD,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ marginBottom: token.marginSM }}>
        <Text strong style={{ display: 'block' }}>
          {title}
        </Text>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {hint}
        </Text>
      </div>

      {/* 0) 產品分類 autocomplete (groups only) — prefills the two names.
             Search-first over the taxonomy with a browse fallback: the shared
             TaxonomyCascader, never a forked category control. */}
      {categoryPicker && (
        <>
          <Text style={{ ...formStyles.labelStyle, marginBottom: token.marginXXS }}>
            {t('pricing:pricing.quickCreate.category')}
          </Text>
          <Controller
            name="categoryId"
            control={control}
            render={({ field }) => (
              <TaxonomyCascader
                value={field.value ?? null}
                onChange={(categoryId) => {
                  field.onChange(categoryId);
                  const node = categoryId ? categoryLookup?.(categoryId) : null;
                  if (!node) return;
                  // PREFILL the names; the code stays a suggestion derived
                  // from the EN name (never bonded to the category id).
                  setValue('nameZh', node.nameZh, { shouldValidate: true });
                  setValue('nameEn', node.nameEn, { shouldValidate: true });
                  if (!codeEdited) {
                    setValue('code', suggestPricingCode(codePrefix, node.nameEn, takenCodes), {
                      shouldValidate: false,
                    });
                    dispatch({ type: 'CODE_EDITED' });
                  }
                }}
                texts={{
                  placeholder: (label) => t('pricing:pricing.quickCreate.categoryLevel', { label }),
                  breadcrumbLabel: t('pricing:pricing.quickCreate.categoryBreadcrumb'),
                }}
              />
            )}
          />
          <Text
            type="secondary"
            style={{ display: 'block', margin: `4px 0 ${token.marginSM}px`, fontSize: token.fontSizeSM }}
          >
            {t('pricing:pricing.quickCreate.categoryHint')}
          </Text>
        </>
      )}

      {/* 0b) 群組標準折扣 — the policy rate an operator reads off the group. */}
      {categoryPicker && (
        <>
          <Text style={formStyles.labelStyle}>
            {t('pricing:pricing.quickCreate.defaultDiscount')}
          </Text>
          <Controller
            name="defaultDiscountPct"
            control={control}
            render={({ field }) => (
              <InputNumber
                value={field.value ?? null}
                onChange={(next) => field.onChange(next)}
                min={0}
                max={100}
                size="large"
                addonAfter="%"
                style={{ ...inputStyle, width: '100%' }}
              />
            )}
          />
          <Text
            type="secondary"
            style={{ display: 'block', margin: `4px 0 ${token.marginSM}px`, fontSize: token.fontSizeSM }}
          >
            {t('pricing:pricing.quickCreate.defaultDiscountHint')}
          </Text>
        </>
      )}

      {/* 1) 名稱（中文）— primary, autofocused */}
      <Text style={formStyles.labelStyle}>
        {requiredMark}
        {t('pricing:pricing.quickCreate.nameZh')}
      </Text>
      <Controller
        name="nameZh"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            autoFocus
            size="large"
            style={inputStyle}
            status={errors.nameZh ? 'error' : undefined}
            onBlur={() => {
              field.onBlur();
              maybeSuggestCode();
            }}
          />
        )}
      />
      {fieldError('nameZh')}

      {/* 2) 名稱（英文）*/}
      <Text style={{ ...formStyles.labelStyle, marginTop: token.marginSM }}>
        {requiredMark}
        {t('pricing:pricing.quickCreate.nameEn')}
      </Text>
      <Controller
        name="nameEn"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            size="large"
            style={inputStyle}
            status={errors.nameEn ? 'error' : undefined}
            onBlur={() => {
              field.onBlur();
              maybeSuggestCode();
            }}
          />
        )}
      />
      {fieldError('nameEn')}

      {/* 3) 代碼 — auto-suggested, editable, availability-checked on blur */}
      <Text style={{ ...formStyles.labelStyle, marginTop: token.marginSM }}>
        {requiredMark}
        {t('pricing:pricing.quickCreate.code')}
      </Text>
      <Controller
        name="code"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            size="large"
            style={inputStyle}
            status={errors.code || status === 'code_taken' ? 'error' : undefined}
            onChange={(event) => {
              field.onChange(event);
              handleCodeUserEdit();
            }}
            onBlur={() => {
              field.onBlur();
              handleCodeBlur();
            }}
          />
        )}
      />
      {errors.code && (touchedFields.code || errors.code?.type === 'server') ? (
        <Text style={formStyles.errorStyle}>{t(errors.code?.message ?? '')}</Text>
      ) : status === 'code_taken' ? (
        <Text style={formStyles.errorStyle}>{t('pricing:pricing.quickCreate.codeTaken')}</Text>
      ) : status === 'code_available' ? (
        <Text style={{ ...formStyles.errorStyle, color: token.colorSuccess }}>
          <CheckCircleFilled style={{ marginRight: token.marginXXS }} />
          {t('pricing:pricing.quickCreate.codeAvailable')}
        </Text>
      ) : codeValue && !codeEdited ? (
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: token.marginXXS, fontSize: token.fontSizeSM }}
        >
          {t('pricing:pricing.quickCreate.codeSuggested')}
        </Text>
      ) : null}

      {status === 'failure' && (
        <Text style={{ ...formStyles.errorStyle, display: 'block', marginTop: token.marginXS }}>
          {t('common:messages.createFailed')}
        </Text>
      )}

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: token.marginXS,
          marginTop: token.marginSM,
        }}
      >
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={saving}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: radiusTokens.control,
            flex: '1 1 120px',
          }}
        >
          {t('common:actions.save')}
        </Button>
        <Button
          type="text"
          htmlType="button"
          size="large"
          disabled={saving}
          onClick={onCancel}
          style={{
            height: `${controlHeightTokens.controlHeightXL}px`,
            borderRadius: radiusTokens.control,
          }}
        >
          {t('common:actions.cancel')}
        </Button>
      </div>

      {/* Visually-hidden polite announcer: success / taken / failure copy. */}
      <div aria-live="polite" className="tw-sr-only">
        {liveMessage}
      </div>

      {reasonCopy && (
        <ReasonRequiredModal
          open={reasonOpen}
          title={reasonCopy.title}
          description={reasonCopy.description}
          reasonLabel={reasonCopy.reasonLabel}
          reasonPlaceholder={reasonCopy.reasonPlaceholder}
          requiredMessage={reasonCopy.requiredMessage}
          submitText={reasonCopy.submitText}
          cancelText={t('common:actions.cancel')}
          onSubmit={(reason) => {
            setReasonOpen(false);
            const pending = pendingValuesRef.current;
            if (pending) void runSave(pending, reason);
          }}
          onCancel={() => setReasonOpen(false)}
        />
      )}
    </form>
  );
};

const GroupMatrixTab = () => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const formStyles = useFormStyles();

  const { dataset, isLoading, error, refresh } = usePricingDataset();
  // S1.4 — every write on this tab rides usePricingMutations (the recorded
  // S5.2 consolidation follow-up): reason threading, actor attribution and
  // cache invalidation live in the hook, never at the call site.
  const {
    saveMarkupCell,
    createPriceLevel,
    addPricingGroup,
    saveQuantitySchedule,
    updatePricingGroup,
    setPricingGroupActive,
    saveCustomerGroupDiscount,
    addPriceGroupBand,
    updatePriceGroupBand,
    setPriceGroupBandActive,
    moveBandCut,
  } = usePricingMutations();
  const { currencyOptions } = useCurrencies();
  const elevation = useElevation();

  // Popover chrome carries the elevation (host-applied, taxonomy precedent).
  const {
    border: popoverBorder,
    boxShadow: popoverShadow,
    borderRadius: popoverRadius,
  } = elevation.popover;
  const popoverBodyStyle = useMemo(
    () => ({
      padding: 0,
      border: popoverBorder,
      boxShadow: popoverShadow,
      borderRadius: popoverRadius,
    }),
    [popoverBorder, popoverShadow, popoverRadius]
  );

  const [groupCreateOpen, setGroupCreateOpen] = useState(false);
  /** null = closed · 'create' = new schedule · schedule = editing that row. */
  const [editingSchedule, setEditingSchedule] = useState<
    QuantityPricingSchedule | 'create' | null
  >(null);
  /** S2.1 — the group being edited (null = modal closed). */
  const [editingGroup, setEditingGroup] = useState<PricingGroup | null>(null);
  /** S2.1 — the reason-gated group action awaiting its ReasonRequiredModal. */
  const [pendingGroupAction, setPendingGroupAction] = useState<PendingGroupAction | null>(null);
  /** S2.3 — null = closed · 'create' = new 折扣卡 · card = editing that row. */
  const [editingCard, setEditingCard] = useState<CustomerGroupDiscount | 'create' | null>(null);
  /**
   * P2 — which BAND SET the band card is showing. `DEFAULT_BAND_SET_SCOPE` is
   * the unscoped default set; any other value is the owning product group's
   * category id. The matrix above always renders the DEFAULT set (a scoped row
   * beside a default one would put two "Group B" columns on screen with
   * nothing saying which set each belongs to); this card is where a scoped set
   * becomes visible and editable, which is the surface P1 deferred to here.
   */
  const [bandScope, setBandScope] = useState<string>(DEFAULT_BAND_SET_SCOPE);
  /** null = closed · 'create' = new band · band = editing that row. */
  const [editingBand, setEditingBand] = useState<PriceGroupBand | 'create' | null>(null);
  /** P2 — the reason-gated band action awaiting its ReasonRequiredModal. */
  const [pendingBandAction, setPendingBandAction] = useState<PendingBandAction | null>(null);
  /**
   * The REFUSAL, kept on screen until the operator changes something. It names
   * the two cuts and the two numbers that conflict — the store never repairs a
   * cover, so the message has to be specific enough to act on.
   */
  const [bandViolation, setBandViolation] = useState<BandSetViolation | null>(null);

  // Taxonomy read for the group↔category link (read-back column + the
  // create-form autocomplete). Search-first picker is the shared
  // TaxonomyCascader — never a forked category control.
  const { nodes: categoryNodes } = useTaxonomyTree();
  const categoryById = useMemo(
    () => new Map(categoryNodes.map((node) => [node.id, node])),
    [categoryNodes]
  );
  const zh = i18n.language.startsWith('zh');
  /** Bilingual full path — the research's "indent/lighten the hierarchy path". */
  const categoryPath = useCallback(
    (categoryId: string): string => {
      const node = categoryById.get(categoryId);
      if (!node) return categoryId;
      const names: string[] = [];
      for (const id of node.path.split('/')) {
        const step = categoryById.get(id);
        if (step) names.push(zh ? step.nameZh : step.nameEn);
      }
      return names.length > 0 ? names.join(' / ') : zh ? node.nameZh : node.nameEn;
    },
    [categoryById, zh]
  );

  const pricingGroups = useMemo(() => dataset?.pricingGroups ?? [], [dataset]);
  const quantitySchedules = useMemo(() => dataset?.quantityPricingSchedules ?? [], [dataset]);
  const groupCodes = useMemo(() => pricingGroups.map((group) => group.code), [pricingGroups]);
  const scheduleCodes = useMemo(
    () => quantitySchedules.map((schedule) => schedule.code),
    [quantitySchedules]
  );
  // S2.3 — the customer 折扣卡 rows this tab now manages (UI seat for the
  // shipped saveCustomerGroupDiscount; add + edit only — the union carries no
  // isActive member, and adding one would be an unnamed schema change
  // (refused this wave, the S2.2 validity-fields precedent)).
  const discountCards = useMemo(() => dataset?.customerGroupDiscounts ?? [], [dataset]);
  const groupById = useMemo(
    () => new Map(pricingGroups.map((group) => [group.id, group])),
    [pricingGroups]
  );
  const levelById = useMemo(
    () => new Map((dataset?.priceLevels ?? []).map((level) => [level.id, level])),
    [dataset]
  );

  const codeSlug = (code: string) =>
    code
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');

  /** Group CREATION is not price-affecting until a card references it — no reason gate. */
  const saveNewGroup = (values: QuickCreateFields) => {
    addPricingGroup({
      id: `pgrp-${codeSlug(values.code)}-${Date.now()}`,
      code: values.code,
      nameEn: values.nameEn,
      nameZh: values.nameZh,
      // The picked taxonomy node, when the operator used the autocomplete.
      ...(values.categoryId ? { categoryId: values.categoryId } : {}),
      ...(values.defaultDiscountPct != null
        ? { defaultDiscountPct: values.defaultDiscountPct }
        : {}),
      isActive: true,
    });
  };

  /**
   * Schedule save IS price-affecting — the reason threads through from the
   * editor's own ReasonRequiredModal. One handler for create AND edit: the
   * store reducer upserts by id, so editing keeps the id (and every product
   * reference to it) while a create mints one.
   */
  const saveSchedule = (values: ScheduleFormValues, reason: string) => {
    const existing = editingSchedule !== 'create' ? editingSchedule : null;
    saveQuantitySchedule(
      {
        id: existing?.id ?? `qs-${codeSlug(values.code)}-${Date.now()}`,
        code: values.code.trim(),
        nameEn: values.nameEn.trim(),
        nameZh: values.nameZh.trim(),
        basis: 'by_line_quantity',
        brackets: [...values.brackets].sort((a, b) => a.minQuantity - b.minQuantity),
        // An absent side is an OPEN end — never a fabricated far-future date.
        ...(values.appliesFrom ? { appliesFrom: values.appliesFrom } : {}),
        ...(values.appliesTo ? { appliesTo: values.appliesTo } : {}),
        isActive: existing?.isActive ?? true,
      },
      reason
    );
    setEditingSchedule(null);
    message.success(t('pricing:pricing.schedulesAdmin.messages.saved'));
  };

  // -------------------------------------------------------------------------
  // S2.1 — group edit + activate/deactivate (reason-gated store actions)
  // -------------------------------------------------------------------------

  const {
    control: groupControl,
    handleSubmit: handleGroupSubmit,
    reset: groupReset,
    formState: { errors: groupErrors },
  } = useForm<GroupEditFormData>({
    resolver: zodResolver(groupEditSchema),
    defaultValues: { nameZh: '', nameEn: '', defaultDiscountPct: null, categoryId: null },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // DA-8 — ≥100% is WARN-ONLY on the group's policy rate too (same banner as
  // the bracket editors; a 100% rate zeroes every card it seeds).
  const watchedGroupDiscount = useWatch({ control: groupControl, name: 'defaultDiscountPct' });

  const openGroupEdit = (group: PricingGroup) => {
    setEditingGroup(group);
    groupReset({
      nameZh: group.nameZh,
      nameEn: group.nameEn,
      defaultDiscountPct: group.defaultDiscountPct ?? null,
      categoryId: group.categoryId ?? null,
    });
  };

  const closeGroupEdit = () => setEditingGroup(null);

  /** Form OK → build the patch, then demand the reason (never save without it). */
  const onGroupEditValid = (values: GroupEditFormData) => {
    if (!editingGroup) return;
    setPendingGroupAction({
      kind: 'update',
      groupId: editingGroup.id,
      patch: {
        nameZh: values.nameZh.trim(),
        nameEn: values.nameEn.trim(),
        defaultDiscountPct: values.defaultDiscountPct,
        categoryId: values.categoryId,
      },
    });
  };

  const onGroupReasonSubmit = (reason: string) => {
    if (!pendingGroupAction) return;
    try {
      if (pendingGroupAction.kind === 'update') {
        updatePricingGroup(pendingGroupAction.groupId, pendingGroupAction.patch, reason);
        message.success(t('pricing:pricing.groupsAdmin.messages.updated'));
        setEditingGroup(null);
      } else {
        setPricingGroupActive(pendingGroupAction.groupId, pendingGroupAction.isActive, reason);
        message.success(t('pricing:pricing.groupsAdmin.messages.statusUpdated'));
      }
      setPendingGroupAction(null);
    } catch {
      message.error(t('pricing:pricing.groupsAdmin.messages.saveFailed'));
    }
  };

  /** S2.3 — the card modal hands back (row, reason); the store guard enforces both. */
  const saveDiscountCard = (cardRow: CustomerGroupDiscount, reason: string) => {
    try {
      saveCustomerGroupDiscount(cardRow, reason);
      setEditingCard(null);
      message.success(t('pricing:pricing.discountsAdmin.messages.saved'));
    } catch {
      message.error(t('pricing:pricing.discountsAdmin.messages.saveFailed'));
    }
  };

  const allBands = useMemo(() => dataset?.priceGroupBands ?? [], [dataset]);

  /**
   * M1 (2026-09-01) — THE MATRIX FOLLOWS THE SELECTED SET.
   *
   * This used to be a hard `band.productGroupId === undefined` filter: the
   * grid was pinned to the DEFAULT set while the "Band set" selector below it
   * drove only the Price Bands table. Melissa's report — *"the price level's
   * bound should change with different product group but the ui not see
   * anywhere can set this per price level for product group select"* — is
   * exactly that: a product group's own bounds were listable and its markups
   * were reachable from no screen at all.
   *
   * The P1/P2 docblock's worry (two "Group B" columns with nothing saying
   * which set each belongs to) is answered by SHOWING ONE SET AT A TIME and
   * NAMING it above the grid, not by hiding the scoped sets. The selection
   * lives in the pure `matrixScope` module because the node test env cannot
   * render this file (`erp-web-gate-reality` §2), and the guard imports the
   * same body the component does.
   */
  const bandCurrencies = useMemo(
    () => bandSetCurrencies(allBands, bandScope),
    [allBands, bandScope]
  );

  // 使用者選擇 — the user picks the matrix currency; the pick is honoured
  // whenever the SELECTED SET has bands in it (see `matrixCurrencyFor`).
  const [currency, setCurrency] = useState<string | null>(null);
  const activeCurrency = matrixCurrencyFor(bandCurrencies, currency);

  const currencySelectOptions = useMemo(() => {
    const known = new Set(currencyOptions.map((option) => option.value as string));
    const extras = bandCurrencies
      .filter((code) => !known.has(code))
      .map((code) => ({ label: code, value: code }));
    return [...currencyOptions, ...extras];
  }, [currencyOptions, bandCurrencies]);

  const bands = useMemo(
    () => matrixColumnBands(allBands, bandScope, activeCurrency),
    [allBands, bandScope, activeCurrency]
  );

  // -------------------------------------------------------------------------
  // P2 — PRICE BAND CRUD (價格帶). The write path the engine never had: bands
  // were seed-only until this slice, so a set could be read and never cut.
  //
  // Every action here is reason-gated and audited, because a band is what
  // CHOOSES the markup rule a level price is computed from (RULING R-BAND) —
  // moving a cut moves prices. The half-open / gapless / non-overlapping rule
  // is NOT implemented in this file: the store's reducers enforce the pure
  // `bandSetInvariants` body and throw a violation carrying the two cuts and
  // the two numbers, which is what `bandViolation` renders. Nothing here
  // repairs a cover, and nothing here re-cuts a seeded band (R-AD).
  // -------------------------------------------------------------------------

  /** Depth-2 taxonomy nodes — the only level that may own a band set. */
  const productGroupNodes = useMemo(
    () => categoryNodes.filter((node) => node.depth === 2 && node.isActive),
    [categoryNodes]
  );

  /**
   * ONE name for a band set, used by the selector options, the matrix caption
   * and the band editor's read-only scope tag alike. A second spelling of
   * "which set is this" is how P3's currency matrix once captioned one set's
   * name over another set's bounds — a confident wrong answer about which
   * numbers the reader is looking at.
   */
  const bandSetLabel = useCallback(
    (scope: string): string =>
      scope === DEFAULT_BAND_SET_SCOPE
        ? t('pricing:pricing.bandsAdmin.defaultSet')
        : categoryPath(scope),
    [categoryPath, t]
  );

  /** The sets that EXIST — default first, then every group that owns rows. */
  const bandScopeOptions = useMemo(() => {
    const owned = [...new Set(allBands.map((band) => bandSetScopeOf(band)))].filter(
      (scope) => scope !== DEFAULT_BAND_SET_SCOPE
    );
    return [
      { label: bandSetLabel(DEFAULT_BAND_SET_SCOPE), value: DEFAULT_BAND_SET_SCOPE },
      ...owned.map((scope) => ({ label: bandSetLabel(scope), value: scope })),
    ];
  }, [allBands, bandSetLabel]);

  /**
   * Every set a band may be CREATED in — the default set plus every depth-2
   * node, including groups that own nothing yet. This is how a product group
   * gets its FIRST cut; the read selector above only lists sets that exist.
   */
  const bandScopeCreateOptions = useMemo(
    () => [
      { label: bandSetLabel(DEFAULT_BAND_SET_SCOPE), value: DEFAULT_BAND_SET_SCOPE },
      ...productGroupNodes.map((node) => ({ label: bandSetLabel(node.id), value: node.id })),
    ],
    [productGroupNodes, bandSetLabel]
  );

  /**
   * The selected set's rows, ALL currencies. The partition the invariant
   * adjudicates is (set × currency), so the currency is a COLUMN here rather
   * than a second selector — the operator sees the whole set at once, and D27
   * (bands are per-currency objects, never FX-converted) reads off the table
   * instead of being hidden behind a filter.
   *
   * M1: the SAME `bandsInSet` body the matrix columns come from, so the table
   * cannot list a band the grid has no column for.
   */
  const scopedBands = useMemo(() => bandsInSet(allBands, bandScope), [allBands, bandScope]);

  const {
    control: bandControl,
    handleSubmit: handleBandSubmit,
    reset: bandReset,
    formState: { errors: bandErrors },
  } = useForm<BandEditFormData>({
    resolver: zodResolver(bandEditSchema),
    defaultValues: {
      scope: DEFAULT_BAND_SET_SCOPE,
      currency: '',
      group: 'A',
      minInclusive: 0,
      maxExclusive: null,
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  /**
   * P3 — the neighbour bound(s) the CURRENT form values would drag with them,
   * recomputed live from the SAME pure planner the save routes through. It is
   * shown BEFORE the operator commits, because a two-row write has to be a
   * disclosed decision rather than something found in the audit trail
   * afterwards. Empty for a create, and empty for an edit that touches no
   * shared boundary (which stays the strict single-row path).
   */
  const watchedBandMin = useWatch({ control: bandControl, name: 'minInclusive' });
  const watchedBandMax = useWatch({ control: bandControl, name: 'maxExclusive' });
  const bandCutPreview = useMemo<BandCutMove[]>(() => {
    if (editingBand === null || editingBand === 'create') return [];
    return planBandCutMoves(allBands, editingBand.id, {
      minInclusive: typeof watchedBandMin === 'number' ? watchedBandMin : undefined,
      maxExclusive: watchedBandMax === undefined ? undefined : watchedBandMax,
    });
  }, [allBands, editingBand, watchedBandMin, watchedBandMax]);

  const openBandCreate = () => {
    setBandViolation(null);
    setEditingBand('create');
    bandReset({
      scope: bandScope,
      // Seed from the set being viewed so the common case is one field less;
      // an empty set falls back to the matrix currency, then to blank.
      currency: scopedBands[0]?.currency ?? activeCurrency ?? '',
      group: 'A',
      minInclusive: 0,
      maxExclusive: null,
    });
  };

  const openBandEdit = (band: PriceGroupBand) => {
    setBandViolation(null);
    setEditingBand(band);
    bandReset({
      scope: bandSetScopeOf(band),
      currency: band.currency,
      group: band.group,
      minInclusive: band.minInclusive,
      maxExclusive: band.maxExclusive,
    });
  };

  const closeBandEditor = () => {
    setEditingBand(null);
    setBandViolation(null);
  };

  /** Form OK → build the request, then demand the reason (never save without it). */
  const onBandEditValid = (values: BandEditFormData) => {
    setBandViolation(null);
    if (editingBand === 'create') {
      setPendingBandAction({
        kind: 'create',
        create: {
          id: `pgb-${codeSlug(values.scope)}-${codeSlug(values.currency)}-${values.group.toLowerCase()}-${Date.now()}`,
          group: values.group,
          minInclusive: values.minInclusive,
          maxExclusive: values.maxExclusive,
          currency: values.currency.trim().toUpperCase(),
          // Absent for the default set — the scope is a row a band CARRIES.
          ...(values.scope === DEFAULT_BAND_SET_SCOPE ? {} : { productGroupId: values.scope }),
        },
      });
      return;
    }
    if (editingBand === null) return;
    // Cuts only — the scope and currency the form showed were read-only.
    const patch: PriceGroupBandPatch = {
      group: values.group,
      minInclusive: values.minInclusive,
      maxExclusive: values.maxExclusive,
    };
    // P3 — does this edit move a SHARED boundary? A cover is gapless, so a
    // shared bound is ONE number on two rows and neither row can move alone
    // (shrink-first leaves a gap, widen-first an overlap; both are refused).
    // When the plan is non-empty the editor has already NAMED the neighbour
    // and both numbers, so the save routes to the atomic two-row action.
    const moves = planBandCutMoves(allBands, editingBand.id, patch);
    setPendingBandAction(
      moves.length > 0
        ? { kind: 'moveCut', bandId: editingBand.id, patch, moves }
        : { kind: 'update', bandId: editingBand.id, patch }
    );
  };

  /**
   * The ONE reason gate for all three band actions. A guard refusal is shown
   * as its NAMED boundary conflict and the editor stays open — never a silent
   * repair, and never a bare "save failed" for a fault the operator can fix.
   */
  const onBandReasonSubmit = (reason: string) => {
    if (!pendingBandAction) return;
    try {
      if (pendingBandAction.kind === 'create') {
        addPriceGroupBand(pendingBandAction.create, reason);
        message.success(t('pricing:pricing.bandsAdmin.messages.saved'));
        // Follow the operator to the set they just cut into.
        setBandScope(pendingBandAction.create.productGroupId ?? DEFAULT_BAND_SET_SCOPE);
        setEditingBand(null);
      } else if (pendingBandAction.kind === 'update') {
        updatePriceGroupBand(pendingBandAction.bandId, pendingBandAction.patch, reason);
        message.success(t('pricing:pricing.bandsAdmin.messages.updated'));
        setEditingBand(null);
      } else if (pendingBandAction.kind === 'moveCut') {
        moveBandCut(pendingBandAction.bandId, pendingBandAction.patch, reason);
        message.success(
          t('pricing:pricing.bandsAdmin.messages.cutMoved', {
            count: pendingBandAction.moves.length,
          })
        );
        setEditingBand(null);
      } else {
        setPriceGroupBandActive(pendingBandAction.bandId, pendingBandAction.isActive, reason);
        message.success(t('pricing:pricing.bandsAdmin.messages.statusUpdated'));
      }
      setPendingBandAction(null);
      setBandViolation(null);
    } catch (error) {
      setPendingBandAction(null);
      if (error instanceof PricingGuardError && error.detail) {
        // The refusal names the two cuts and the two numbers — render it and
        // leave the editor open so the operator can act on it.
        setBandViolation(error.detail);
        return;
      }
      message.error(t('pricing:pricing.bandsAdmin.messages.saveFailed'));
    }
  };

  const activeLevels = useMemo(
    () =>
      (dataset?.priceLevels ?? [])
        .filter((level) => level.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [dataset]
  );

  const ruleIndex = useMemo(() => markupRuleIndex(dataset?.markupRules ?? []), [dataset]);

  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CellFormData>({
    resolver: zodResolver(cellFormSchema),
    defaultValues: { levelEntries: [], newLevels: [] },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const levelEntriesArray = useFieldArray({ control, name: 'levelEntries' });
  const newLevelsArray = useFieldArray({ control, name: 'newLevels' });

  /**
   * M1 — the cell reads THE SELECTED SET'S band id, never the default's.
   * `markupCellKey` is the one key body (guard-pinned against
   * `markupRuleIndex`'s own shape), so a set with no ladder yet reads back
   * `null` for every level and renders 未設定 rather than borrowing the
   * default set's numbers.
   */
  const openCell = (band: PriceGroupBand, source: PriceSource) => {
    setEditingCell({ band, source });
    reset({
      levelEntries: activeLevels.map((level) => ({
        priceLevelId: level.id,
        code: level.code,
        markupPct: ruleIndex.get(markupCellKey(band.id, source, level.id))?.markupPct ?? null,
      })),
      newLevels: [],
    });
  };

  const closeCell = () => {
    setEditingCell(null);
    reset({ levelEntries: [], newLevels: [] });
  };

  const onSaveCell = (values: CellFormData) => {
    if (!editingCell) return;
    const { band, source } = editingCell;

    // 1) New user-defined levels — created matrix-wide, then ruled on this cell.
    const maxSortOrder = Math.max(0, ...activeLevels.map((level) => level.sortOrder));
    const createdLevels: PriceLevel[] = values.newLevels.map((entry, index) => ({
      id: `pl-${entry.code.trim().toLowerCase().replace(/\s+/g, '-')}-${Date.now()}-${index}`,
      code: entry.code.trim(),
      nameEn: entry.nameEn.trim(),
      nameZh: entry.nameZh.trim(),
      sortOrder: maxSortOrder + (index + 1) * 10,
      isActive: true,
    }));
    createdLevels.forEach((level) => createPriceLevel(level));

    // 2) Per-level markups for this Band × Source cell. Every edit is stamped
    //    with the id of the band the operator actually clicked — i.e. the
    //    SELECTED set's row (M1). `markupCellEdits` takes the band ROW rather
    //    than a letter, so "Group B" can never resolve to the default set's
    //    `pg-b` while the tab is showing a product group's own set.
    const edits: MarkupRuleEdit[] = [
      ...markupCellEdits(band, source, values.levelEntries),
      ...markupCellEdits(
        band,
        source,
        createdLevels.map((level, index) => ({
          priceLevelId: level.id,
          markupPct: values.newLevels[index].markupPct,
        }))
      ),
    ];
    saveMarkupCell(edits);

    message.success(t('pricing:pricing.matrix.messages.saved'));
    closeCell();
  };

  const bandRangeLabel = (band: PriceGroupBand): string =>
    band.maxExclusive === null
      ? t('pricing:pricing.matrix.bandOpenEnded', { min: band.minInclusive })
      : t('pricing:pricing.matrix.bandRange', {
          min: band.minInclusive,
          max: band.maxExclusive,
        });

  const columns: TableProps<MatrixRow>['columns'] = [
    {
      title: t('pricing:pricing.matrix.sourceColumn'),
      key: 'source',
      width: 150,
      fixed: 'left',
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>
          {t(`pricing:pricing.sources.${row.source}`)}
        </span>
      ),
    },
    ...bands.map((band) => ({
      title: (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{t('pricing:pricing.matrix.groupColumn', { group: band.group })}</span>
          <span
            style={{
              fontSize: token.fontSizeSM,
              fontWeight: 'normal' as const,
              color: token.colorTextSecondary,
              fontVariantNumeric: 'tabular-nums' as const,
            }}
          >
            {bandRangeLabel(band)} {band.currency}
          </span>
        </div>
      ),
      key: band.id,
      width: 230,
      onCell: (row: MatrixRow) => ({
        onClick: () => openCell(band, row.source),
        style: { cursor: 'pointer' },
      }),
      render: (_: unknown, row: MatrixRow) => {
        // M1 — a set with NO ladder for this cell renders ONE honest 未設定
        // marker, never 17 grey chips and emphatically never the default
        // set's figures (the rules are keyed on THIS band's id).
        if (
          isCellUnset(
            (levelId) => ruleIndex.has(markupCellKey(band.id, row.source, levelId)),
            activeLevels.map((level) => level.id)
          )
        ) {
          return (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.matrix.cellNotSet')}
            </Text>
          );
        }
        return (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {activeLevels.map((level) => {
              const rule = ruleIndex.get(markupCellKey(band.id, row.source, level.id));
              return rule ? (
                <Tag key={level.id} color="purple" style={{ margin: 0 }}>
                  {level.code}{' '}
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {rule.markupPct >= 0 ? '+' : ''}
                    {rule.markupPct}%
                  </span>
                </Tag>
              ) : (
                <Tag key={level.id} style={{ margin: 0, color: token.colorTextTertiary }}>
                  {level.code} · {t('pricing:pricing.matrix.noRule')}
                </Tag>
              );
            })}
          </div>
        );
      },
    })),
  ];

  // With zero bands for the picked currency the two source rows would render a
  // bare 來源 column that reads as broken — empty dataSource instead, so the
  // existing noBands ListEmptyState (locale.emptyText below) becomes reachable.
  const rows: MatrixRow[] = bands.length === 0 ? [] : PRICE_SOURCES.map((source) => ({ source }));

  const fieldErrorText = (key?: string) =>
    key ? <Text style={formStyles.errorStyle}>{t(key)}</Text> : null;

  // -------------------------------------------------------------------------
  // S5.2 admin cards — 定價群組 + 數量定價排程 (inside this tab, no new routes)
  // -------------------------------------------------------------------------

  const statusTag = (isActive: boolean) =>
    isActive ? (
      <Tag color="green" style={{ margin: 0 }}>
        {t('pricing:pricing.overrides.status.active')}
      </Tag>
    ) : (
      <Tag style={{ margin: 0 }}>{t('pricing:pricing.overrides.status.inactive')}</Tag>
    );

  const nameCell = (nameZh: string, nameEn: string) => (
    <div className="tw-flex tw-flex-col tw-gap-0.5">
      <span>{nameZh}</span>
      <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>{nameEn}</span>
    </div>
  );

  const groupColumns: TableProps<PricingGroup>['columns'] = [
    {
      title: t('pricing:pricing.groupsAdmin.columns.code'),
      key: 'code',
      width: 90,
      render: (_: unknown, group) => (
        <Tag color="purple" style={{ margin: 0 }}>
          {group.code}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.groupsAdmin.columns.name'),
      key: 'name',
      render: (_: unknown, group) => nameCell(group.nameZh, group.nameEn),
    },
    {
      // 群組標準折扣 (Melissa 2026-08-28) — the group's policy rate. It seeds
      // new customer cards; it is NEVER applied to a customer without one.
      title: t('pricing:pricing.groupsAdmin.columns.defaultDiscount'),
      key: 'defaultDiscount',
      width: 130,
      align: 'right',
      render: (_: unknown, group) =>
        group.defaultDiscountPct == null ? (
          renderDash()
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {t('pricing:pricing.groupsAdmin.discountValue', { pct: group.defaultDiscountPct })}
          </span>
        ),
    },
    {
      // The linked taxonomy node (Melissa 2026-08-28) — provenance read-back;
      // the group stays a flat list and the resolver never reads it.
      title: t('pricing:pricing.groupsAdmin.columns.category'),
      key: 'category',
      render: (_: unknown, group) =>
        group.categoryId ? (
          <Text style={{ fontSize: token.fontSizeSM }}>{categoryPath(group.categoryId)}</Text>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('pricing:pricing.groupsAdmin.columns.status'),
      key: 'status',
      width: 90,
      render: (_: unknown, group) => statusTag(group.isActive),
    },
    // S2.1 — Edit + Deactivate/Activate in the shared three-dot seat. No hard
    // delete: a deactivated group stays listed (house inactive treatment).
    actionColumn<PricingGroup>((group) => [
      {
        key: 'edit',
        label: t('common:actions.edit'),
        icon: <EditOutlined />,
        onClick: () => openGroupEdit(group),
      },
      {
        key: 'toggle',
        label: group.isActive
          ? t('pricing:pricing.groupsAdmin.actions.deactivate')
          : t('pricing:pricing.groupsAdmin.actions.activate'),
        danger: group.isActive,
        style: !group.isActive ? { color: token.colorSuccess } : undefined,
        onClick: () =>
          setPendingGroupAction({
            kind: 'toggle',
            groupId: group.id,
            isActive: !group.isActive,
          }),
      },
    ]),
  ];

  /**
   * P2 — the band rows of the selected set. Currency is a COLUMN, not a
   * filter: the invariant partition is (set × currency), and showing both
   * currencies of a set side by side is what makes D27 legible instead of
   * hidden behind a selector.
   */
  const bandColumns: TableProps<PriceGroupBand>['columns'] = [
    {
      title: t('pricing:pricing.bandsAdmin.columns.group'),
      key: 'group',
      width: 90,
      render: (_: unknown, band) => (
        <Tag color="purple" style={{ margin: 0 }}>
          {t('pricing:pricing.matrix.groupColumn', { group: band.group })}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.bandsAdmin.columns.currency'),
      key: 'currency',
      width: 110,
      render: (_: unknown, band) => band.currency,
    },
    {
      title: t('pricing:pricing.bandsAdmin.columns.range'),
      key: 'range',
      render: (_: unknown, band) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{bandRangeLabel(band)}</span>
      ),
    },
    {
      title: t('pricing:pricing.bandsAdmin.columns.status'),
      key: 'status',
      width: 90,
      render: (_: unknown, band) => statusTag(isBandActive(band)),
    },
    // Edit + Deactivate/Activate in the shared three-dot seat. No hard delete:
    // a deactivated cut stays listed with the house inactive treatment, and
    // the resolver stops matching it — so deactivating an INTERIOR cut is
    // REFUSED rather than silently absorbed by a neighbour.
    actionColumn<PriceGroupBand>((band) => [
      {
        key: 'edit',
        label: t('common:actions.edit'),
        icon: <EditOutlined />,
        onClick: () => openBandEdit(band),
      },
      {
        key: 'toggle',
        label: isBandActive(band)
          ? t('pricing:pricing.bandsAdmin.actions.deactivate')
          : t('pricing:pricing.bandsAdmin.actions.activate'),
        danger: isBandActive(band),
        style: !isBandActive(band) ? { color: token.colorSuccess } : undefined,
        onClick: () => {
          setBandViolation(null);
          setPendingBandAction({
            kind: 'toggle',
            bandId: band.id,
            isActive: !isBandActive(band),
          });
        },
      },
    ]),
  ];

  /**
   * The refusal, rendered where the operator is looking: inside the editor
   * when a save was refused, above the table when a Deactivate was.
   */
  const bandViolationAlert = bandViolation ? (
    <Alert
      type="error"
      showIcon
      message={t('pricing:pricing.bandsAdmin.violation.title')}
      description={t(BAND_VIOLATION_KEY[bandViolation.code], {
        lowerGroup: bandViolation.lowerGroup ?? '',
        upperGroup: bandViolation.upperGroup ?? '',
        at: bandViolation.at ?? '',
        next: bandViolation.next ?? '',
      })}
      closable
      onClose={() => setBandViolation(null)}
      style={{ marginBottom: token.marginSM }}
    />
  ) : null;

  // S2.3 — customer 折扣卡 list (add/edit via GroupDiscountCardModal).
  const discountColumns: TableProps<CustomerGroupDiscount>['columns'] = [
    {
      title: t('pricing:pricing.discountsAdmin.columns.customer'),
      key: 'customer',
      render: (_: unknown, cardRow) => cardRow.customerName ?? `#${cardRow.customerId}`,
    },
    {
      title: t('pricing:pricing.discountsAdmin.columns.group'),
      key: 'group',
      width: 220,
      render: (_: unknown, cardRow) => {
        const group = groupById.get(cardRow.groupId);
        if (!group) return <Text style={{ fontSize: token.fontSizeSM }}>{cardRow.groupId}</Text>;
        return (
          <span className="tw-inline-flex tw-items-center tw-gap-1.5">
            <Tag color="purple" style={{ margin: 0 }}>
              {group.code}
            </Tag>
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {zh ? group.nameZh : group.nameEn}
            </span>
          </span>
        );
      },
    },
    {
      title: t('pricing:pricing.discountsAdmin.columns.kind'),
      key: 'kind',
      width: 120,
      render: (_: unknown, cardRow) => (
        <Tag color={cardRow.kind === 'discount_pct' ? 'blue' : 'purple'} style={{ margin: 0 }}>
          {t(
            `pricing:pricing.discountsAdmin.kind.${
              cardRow.kind === 'discount_pct' ? 'discountPct' : 'priceLevel'
            }`
          )}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.discountsAdmin.columns.value'),
      key: 'value',
      width: 140,
      align: 'right',
      render: (_: unknown, cardRow) =>
        cardRow.kind === 'discount_pct' ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {t('pricing:pricing.discountsAdmin.valuePct', { pct: cardRow.discountPct })}
          </span>
        ) : (
          <span>{levelById.get(cardRow.priceLevelId)?.code ?? cardRow.priceLevelId}</span>
        ),
    },
    actionColumn<CustomerGroupDiscount>((cardRow) => [
      {
        key: 'edit',
        label: t('common:actions.edit'),
        icon: <EditOutlined />,
        onClick: () => setEditingCard(cardRow),
      },
    ]),
  ];

  const scheduleColumns: TableProps<QuantityPricingSchedule>['columns'] = [
    {
      title: t('pricing:pricing.schedulesAdmin.columns.code'),
      key: 'code',
      width: 110,
      render: (_: unknown, schedule) => (
        <Tag color="purple" style={{ margin: 0 }}>
          {schedule.code}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.schedulesAdmin.columns.name'),
      key: 'name',
      render: (_: unknown, schedule) => nameCell(schedule.nameZh, schedule.nameEn),
    },
    {
      // 適用期間 (Melissa 2026-08-28) — an absent side reads as an OPEN end,
      // and an elapsed period is called out rather than left to arithmetic.
      title: t('pricing:pricing.schedulesAdmin.columns.applyPeriod'),
      key: 'applyPeriod',
      width: 210,
      render: (_: unknown, schedule) => {
        if (!schedule.appliesFrom && !schedule.appliesTo) {
          return (
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.schedulesAdmin.periodAlways')}
            </Text>
          );
        }
        // THREE states, not two (synthesis §1.2C): a schedule can be
        // 未開始 as well as 已結束 — and an inactive one inside its window is
        // 停用, a distinguishable third reason it contributes nothing.
        const today = pricingAsOf();
        const state = !schedule.isActive
          ? 'disabled'
          : schedule.appliesFrom && today < schedule.appliesFrom
            ? 'scheduled'
            : schedule.appliesTo && today > schedule.appliesTo
              ? 'expired'
              : 'active';
        const stateColor: Record<string, string | undefined> = {
          active: 'green',
          scheduled: 'blue',
          expired: 'orange',
          disabled: undefined,
        };
        return (
          <div className="tw-flex tw-flex-col tw-gap-0.5">
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {/* An open side renders as 開放 — never SAP's 31.12.9999,
                  which is a storage convention, not something to show. */}
              {schedule.appliesFrom ?? t('pricing:pricing.schedulesAdmin.periodOpenStart')}
              {' — '}
              {schedule.appliesTo ?? t('pricing:pricing.schedulesAdmin.periodOpenEnd')}
            </span>
            <Tag color={stateColor[state]} style={{ margin: 0, width: 'fit-content' }}>
              {t(`pricing:pricing.schedulesAdmin.periodState.${state}`)}
            </Tag>
          </div>
        );
      },
    },
    {
      // "What breaks if I edit this" — the read-back count (synthesis §1.2C).
      title: t('pricing:pricing.schedulesAdmin.columns.usedBy'),
      key: 'usedBy',
      width: 110,
      align: 'right',
      render: (_: unknown, schedule) => {
        const count = (dataset?.productPricing ?? []).filter(
          (fields) => fields.quantityScheduleId === schedule.id
        ).length;
        return count === 0 ? (
          renderDash()
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {t('pricing:pricing.schedulesAdmin.usedByCount', { count })}
          </span>
        );
      },
    },
    {
      title: t('pricing:pricing.schedulesAdmin.columns.brackets'),
      key: 'brackets',
      render: (_: unknown, schedule) =>
        schedule.brackets.length === 0 ? (
          renderDash()
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {schedule.brackets
              .map((bracket) =>
                t('pricing:pricing.schedulesAdmin.bracketSummary', {
                  min: bracket.minQuantity,
                  pct: bracket.discountPct,
                })
              )
              .join(' / ')}
          </span>
        ),
    },
    {
      title: t('pricing:pricing.schedulesAdmin.columns.status'),
      key: 'status',
      width: 90,
      render: (_: unknown, schedule) => statusTag(schedule.isActive),
    },
    // Created rows must be editable (Melissa 2026-08-28) — same modal as
    // create, so brackets and the period are reachable after the fact. The
    // shared `actionColumn` gives it the app-wide shape: NO header text (an
    // "Actions" title wastes a column of header width to say what the ⋯
    // already says) and every row action inside the three-dot menu.
    actionColumn<QuantityPricingSchedule>((schedule) => [
      {
        key: 'edit',
        label: t('common:actions.edit'),
        icon: <EditOutlined />,
        onClick: () => setEditingSchedule(schedule),
      },
    ]),
  ];

  interface AdminCardProps {
    titleKey: 'groupsAdmin' | 'schedulesAdmin' | 'discountsAdmin' | 'bandsAdmin';
    createOpen: boolean;
    setCreateOpen: (open: boolean) => void;
    /**
     * Popover quick-create content. Absent ⇒ the Add button is a plain
     * button and the host owns the surface — the schedules card needs a full
     * MODAL (dates ＋ insertable brackets), which a popover cannot hold.
     */
    quickCreate?: ReactNode;
    /** Used only when `quickCreate` is absent. */
    onAdd?: () => void;
    table: ReactNode;
  }

  const adminCard = ({
    titleKey,
    createOpen,
    setCreateOpen,
    quickCreate,
    onAdd,
    table,
  }: AdminCardProps) => (
    <div style={{ ...elevation.card, padding: '20px', height: '100%' }}>
      <div className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-mb-3">
        <div>
          <Text strong style={{ display: 'block' }}>
            {t(`pricing:pricing.${titleKey}.title`)}
          </Text>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t(`pricing:pricing.${titleKey}.hint`)}
          </Text>
        </div>
        {quickCreate ? (
          <Popover
            trigger="click"
            open={createOpen}
            onOpenChange={(open) => {
              if (!open) setCreateOpen(false);
            }}
            placement="bottomRight"
            destroyOnHidden
            styles={{ body: popoverBodyStyle }}
            content={createOpen ? quickCreate : null}
          >
            <Button
              icon={<PlusOutlined />}
              size="large"
              style={{ height: `${controlHeightTokens.controlHeightXL}px`, flexShrink: 0 }}
              onClick={() => setCreateOpen(true)}
            >
              {t(`pricing:pricing.${titleKey}.addButton`)}
            </Button>
          </Popover>
        ) : (
          <Button
            icon={<PlusOutlined />}
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px`, flexShrink: 0 }}
            onClick={onAdd}
          >
            {t(`pricing:pricing.${titleKey}.addButton`)}
          </Button>
        )}
      </div>
      {table}
    </div>
  );

  return (
    <>
      {/* M1 — ONE band-set selector governs the WHOLE tab: the matrix columns,
          the cells those columns edit, and the Price Bands table below. It
          sits FIRST because the currency options depend on it (a set only has
          the currencies it holds bands in). Currency stays a user selector
          (W9b「使用者選擇」) — never hardcoded tabs. */}
      <div className="tw-mb-3 tw-flex tw-gap-3 tw-items-center tw-flex-wrap">
        <div>
          <Text style={formStyles.labelStyle}>
            {t('pricing:pricing.bandsAdmin.setLabel')}
          </Text>
          <Select
            size="large"
            value={bandScope}
            onChange={(value) => {
              setBandScope(value);
              setBandViolation(null);
            }}
            options={bandScopeOptions}
            style={{ width: '320px' }}
            showSearch
            optionFilterProp="label"
            aria-label={t('pricing:pricing.bandsAdmin.setLabel')}
          />
        </div>
        <div>
          <Text style={formStyles.labelStyle}>{t('pricing:pricing.matrix.currencyLabel')}</Text>
          <Select
            size="large"
            value={activeCurrency}
            onChange={(value) => setCurrency(value)}
            options={currencySelectOptions}
            style={{ width: '260px' }}
            aria-label={t('pricing:pricing.matrix.currencyLabel')}
          />
        </div>
        <Text
          type="secondary"
          style={{ fontSize: token.fontSizeSM, alignSelf: 'flex-end', paddingBottom: '10px' }}
        >
          {t('pricing:pricing.matrix.setHint')}
        </Text>
      </div>

      {/* The grid NAMES the set it is showing. P1 deferred the scoped sets
          because two "Group B" columns with nothing saying whose they are
          reads as a duplicate-data defect; the answer is one set at a time
          with its owner's NAME on it, not a hidden set. */}
      <div className="tw-mb-3">
        <Text strong style={{ display: 'block' }}>
          {t('pricing:pricing.matrix.setCaption', { set: bandSetLabel(bandScope) })}
        </Text>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          {t('pricing:pricing.matrix.currencyHint')} · {t('pricing:pricing.matrix.cellHint')}
        </Text>
      </div>

      <TableContainer
        errorState={{
          active: !!error && !dataset,
          title: t('pricing:pricing.matrix.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<MatrixRow>
          columns={columns}
          dataSource={rows}
          rowKey="source"
          loading={isLoading}
          pagination={false}
          locale={{
            // The empty grid NAMES the set and the currencies that set does
            // hold — the operator's currency pick is never overridden, so
            // this state is reachable and has to be actionable. A set that
            // holds nothing at all gets the bare title (there is no list of
            // currencies to offer, and inventing one would be worse).
            emptyText: (
              <ListEmptyState
                title={t('pricing:pricing.matrix.states.noBands')}
                description={
                  bandCurrencies.length > 0
                    ? t('pricing:pricing.matrix.states.noBandsHint', {
                        set: bandSetLabel(bandScope),
                        currencies: bandCurrencies.join(' · '),
                      })
                    : undefined
                }
              />
            ),
          }}
          scroll={{ x: 150 + bands.length * 230 }}
        />
      </TableContainer>

      {/* P2 — 價格帶 CRUD, directly under the matrix whose COLUMNS these bands
          ARE. M1: the card no longer carries its OWN set selector — the one
          at the top of the tab governs both, so what you see in the grid and
          what you edit in this table are the same set by construction. */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          {adminCard({
            titleKey: 'bandsAdmin',
            // Scope ＋ currency ＋ two bounds need a full modal, so the card
            // renders a plain Add button (schedules-card precedent).
            createOpen: false,
            setCreateOpen: () => {},
            onAdd: openBandCreate,
            table: (
              <>
                {/* No second selector: the table follows the tab's one set,
                    and says so, rather than offering a rival control that can
                    disagree with the grid above it. */}
                <div className="tw-mb-3">
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {t('pricing:pricing.bandsAdmin.setFollows', {
                      set: bandSetLabel(bandScope),
                    })}
                  </Text>
                </div>
                {/* A Deactivate refusal has no modal to land in — show it here. */}
                {editingBand === null ? bandViolationAlert : null}
                <TableContainer
                  errorState={{
                    active: !!error && !dataset,
                    title: t('pricing:pricing.bandsAdmin.states.loadFailed'),
                    retryLabel: t('common:errors.boundary.retry'),
                    onRetry: refresh,
                  }}
                >
                  <Table<PriceGroupBand>
                    columns={bandColumns}
                    dataSource={scopedBands}
                    rowKey="id"
                    size="small"
                    loading={isLoading}
                    pagination={false}
                    rowClassName={(record) => (!isBandActive(record) ? 'inactive-row' : '')}
                    locale={{
                      emptyText: (
                        <ListEmptyState
                          title={t('pricing:pricing.bandsAdmin.states.empty')}
                          description={t('pricing:pricing.bandsAdmin.states.emptyHint')}
                        />
                      ),
                    }}
                  />
                </TableContainer>
              </>
            ),
          })}
        </Col>
      </Row>

      {/* S5.2 — schedule/group management INSIDE this tab (no new routes).
          Both cards declare explicit empty + error states (ux-lens). */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        {/* Widths follow column COUNT (Melissa 2026-08-28): the schedules
            card carries period + products + brackets and needs the room; the
            groups card reads comfortably narrower. */}
        <Col xs={24} xl={9}>
          {adminCard({
            titleKey: 'groupsAdmin',
            createOpen: groupCreateOpen,
            setCreateOpen: setGroupCreateOpen,
            quickCreate: (
              <PricingQuickCreateForm
                title={t('pricing:pricing.groupsAdmin.quickCreate.title')}
                hint={t('pricing:pricing.groupsAdmin.quickCreate.hint')}
                codePrefix="PG"
                takenCodes={groupCodes}
                // Category autocomplete (Melissa 2026-08-28): picking a node
                // PREFILLS the two names and suggests a code from them; the
                // code is never DERIVED from the category (no ERP does — a
                // derived code breaks the rename/move story).
                categoryPicker
                categoryLookup={(categoryId) => categoryById.get(categoryId)}
                performSave={(values) => saveNewGroup(values)}
                onSaved={() => {
                  setGroupCreateOpen(false);
                  message.success(t('pricing:pricing.groupsAdmin.messages.saved'));
                }}
                onCancel={() => setGroupCreateOpen(false)}
              />
            ),
            table: (
              <TableContainer
                errorState={{
                  active: !!error && !dataset,
                  title: t('pricing:pricing.groupsAdmin.states.loadFailed'),
                  retryLabel: t('common:errors.boundary.retry'),
                  onRetry: refresh,
                }}
              >
                <Table<PricingGroup>
                  columns={groupColumns}
                  dataSource={pricingGroups}
                  rowKey="id"
                  size="small"
                  loading={isLoading}
                  pagination={false}
                  rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
                  locale={{
                    emptyText: (
                      <ListEmptyState
                        title={t('pricing:pricing.groupsAdmin.states.empty')}
                        description={t('pricing:pricing.groupsAdmin.states.emptyHint')}
                      />
                    ),
                  }}
                />
              </TableContainer>
            ),
          })}
        </Col>
        <Col xs={24} xl={15}>
          {adminCard({
            titleKey: 'schedulesAdmin',
            // The schedules surface is a MODAL (period ＋ insertable
            // brackets), so the card renders a plain Add button.
            createOpen: false,
            setCreateOpen: () => {},
            onAdd: () => setEditingSchedule('create'),
            table: (
              <TableContainer
                errorState={{
                  active: !!error && !dataset,
                  title: t('pricing:pricing.schedulesAdmin.states.loadFailed'),
                  retryLabel: t('common:errors.boundary.retry'),
                  onRetry: refresh,
                }}
              >
                <Table<QuantityPricingSchedule>
                  columns={scheduleColumns}
                  dataSource={quantitySchedules}
                  rowKey="id"
                  size="small"
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: (
                      <ListEmptyState
                        title={t('pricing:pricing.schedulesAdmin.states.empty')}
                        description={t('pricing:pricing.schedulesAdmin.states.emptyHint')}
                      />
                    ),
                  }}
                />
              </TableContainer>
            ),
          })}
        </Col>
      </Row>

      {/* S2.3 — customer 折扣卡 management (the UI seat for the shipped
          saveCustomerGroupDiscount). Add + edit; the pair is the identity
          (one card per customer per group, Discribe-20). */}
      <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
        <Col xs={24}>
          {adminCard({
            titleKey: 'discountsAdmin',
            // Pickers + kind switch need a full modal, so the card renders a
            // plain Add button (schedules-card precedent).
            createOpen: false,
            setCreateOpen: () => {},
            onAdd: () => setEditingCard('create'),
            table: (
              <TableContainer
                errorState={{
                  active: !!error && !dataset,
                  title: t('pricing:pricing.discountsAdmin.states.loadFailed'),
                  retryLabel: t('common:errors.boundary.retry'),
                  onRetry: refresh,
                }}
              >
                <Table<CustomerGroupDiscount>
                  columns={discountColumns}
                  dataSource={discountCards}
                  rowKey="id"
                  size="small"
                  loading={isLoading}
                  pagination={false}
                  locale={{
                    emptyText: (
                      <ListEmptyState
                        title={t('pricing:pricing.discountsAdmin.states.empty')}
                        description={t('pricing:pricing.discountsAdmin.states.emptyHint')}
                      />
                    ),
                  }}
                />
              </TableContainer>
            ),
          })}
        </Col>
      </Row>

      {/* Cell edit modal — per-level markups + unlimited add-level rows */}
      <Modal
        open={!!editingCell}
        title={
          editingCell
            ? t('pricing:pricing.matrix.modal.title', {
                group: editingCell.band.group,
                source: t(`pricing:pricing.sources.${editingCell.source}`),
              })
            : ''
        }
        onCancel={closeCell}
        width={640}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeCell}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            loading={isSubmitting}
            onClick={() => void handleSubmit(onSaveCell)()}
          >
            {t('common:actions.save')}
          </Button>,
        ]}
      >
        {editingCell && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: token.marginMD }}>
              {t('pricing:pricing.matrix.modal.bandLabel')}
              {': '}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {bandRangeLabel(editingCell.band)} {editingCell.band.currency}
              </span>
              {' · '}
              {t('pricing:pricing.matrix.modal.markupHint')}
            </Text>

            <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '32px', padding: '20px' }}>
              <Text strong style={formStyles.sectionTitleStyle}>
                {t('pricing:pricing.matrix.modal.levelColumn')}
              </Text>
              {levelEntriesArray.fields.map((field, index) => (
                <Row gutter={[16, 16]} key={field.id} style={{ marginTop: index === 0 ? 0 : '12px' }}>
                  <Col span={12}>
                    <Text style={formStyles.labelStyle}>{field.code}</Text>
                  </Col>
                  <Col span={12}>
                    <Controller
                      name={`levelEntries.${index}.markupPct`}
                      control={control}
                      render={({ field: controllerField }) => (
                        <InputNumber
                          value={controllerField.value}
                          onChange={(value) => controllerField.onChange(value ?? null)}
                          onBlur={controllerField.onBlur}
                          size="large"
                          style={{ ...formStyles.inputStyle, width: '100%' }}
                          suffix="%"
                          status={errors.levelEntries?.[index]?.markupPct ? 'error' : undefined}
                          aria-label={`${field.code} ${t('pricing:pricing.matrix.modal.markupColumn')}`}
                        />
                      )}
                    />
                    {fieldErrorText(errors.levelEntries?.[index]?.markupPct?.message)}
                  </Col>
                </Row>
              ))}
            </div>

            {/* Unlimited user-defined levels — never a hardcoded list */}
            <div style={{ ...formStyles.fieldContainerStyle, marginBottom: '12px', padding: '20px' }}>
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
                <Text strong style={{ ...formStyles.sectionTitleStyle, marginBottom: 0 }}>
                  {t('pricing:pricing.matrix.modal.addLevel')}
                </Text>
                <Button
                  icon={<PlusOutlined />}
                  size="large"
                  style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                  onClick={() =>
                    newLevelsArray.append({ code: '', nameEn: '', nameZh: '', markupPct: 0 })
                  }
                >
                  {t('common:actions.add')}
                </Button>
              </div>
              <Text
                type="secondary"
                style={{ fontSize: token.fontSizeSM, display: 'block', marginTop: '8px' }}
              >
                {t('pricing:pricing.matrix.modal.addLevelHint')}
              </Text>
              {newLevelsArray.fields.map((field, index) => (
                <Row gutter={[16, 16]} key={field.id} style={{ marginTop: '12px' }}>
                  <Col span={5}>
                    <Text style={formStyles.labelStyle}>
                      {t('pricing:pricing.matrix.modal.newLevelCode')}
                    </Text>
                    <Controller
                      name={`newLevels.${index}.code`}
                      control={control}
                      render={({ field: controllerField }) => (
                        <Input
                          {...controllerField}
                          size="large"
                          style={formStyles.inputStyle}
                          status={errors.newLevels?.[index]?.code ? 'error' : undefined}
                        />
                      )}
                    />
                    {fieldErrorText(errors.newLevels?.[index]?.code?.message)}
                  </Col>
                  <Col span={7}>
                    <Text style={formStyles.labelStyle}>
                      {t('pricing:pricing.matrix.modal.newLevelNameEn')}
                    </Text>
                    <Controller
                      name={`newLevels.${index}.nameEn`}
                      control={control}
                      render={({ field: controllerField }) => (
                        <Input
                          {...controllerField}
                          size="large"
                          style={formStyles.inputStyle}
                          status={errors.newLevels?.[index]?.nameEn ? 'error' : undefined}
                        />
                      )}
                    />
                    {fieldErrorText(errors.newLevels?.[index]?.nameEn?.message)}
                  </Col>
                  <Col span={7}>
                    <Text style={formStyles.labelStyle}>
                      {t('pricing:pricing.matrix.modal.newLevelNameZh')}
                    </Text>
                    <Controller
                      name={`newLevels.${index}.nameZh`}
                      control={control}
                      render={({ field: controllerField }) => (
                        <Input
                          {...controllerField}
                          size="large"
                          style={formStyles.inputStyle}
                          status={errors.newLevels?.[index]?.nameZh ? 'error' : undefined}
                        />
                      )}
                    />
                    {fieldErrorText(errors.newLevels?.[index]?.nameZh?.message)}
                  </Col>
                  <Col span={4}>
                    <Text style={formStyles.labelStyle}>
                      {t('pricing:pricing.matrix.modal.markupColumn')}
                    </Text>
                    <Controller
                      name={`newLevels.${index}.markupPct`}
                      control={control}
                      render={({ field: controllerField }) => (
                        <InputNumber
                          value={controllerField.value}
                          onChange={(value) => controllerField.onChange(value ?? 0)}
                          onBlur={controllerField.onBlur}
                          size="large"
                          style={{ ...formStyles.inputStyle, width: '100%' }}
                          status={errors.newLevels?.[index]?.markupPct ? 'error' : undefined}
                          aria-label={t('pricing:pricing.matrix.modal.markupColumn')}
                        />
                      )}
                    />
                    {fieldErrorText(errors.newLevels?.[index]?.markupPct?.message)}
                  </Col>
                  <Col span={1} className="tw-flex tw-items-end tw-pb-1">
                    <Button
                      type="text"
                      danger
                      aria-label={t('pricing:pricing.matrix.modal.removeNewLevel')}
                      icon={<DeleteOutlined />}
                      onClick={() => newLevelsArray.remove(index)}
                    />
                  </Col>
                </Row>
              ))}
            </div>
          </>
        )}
      </Modal>

      {/* Schedule create/edit — dates ＋ insertable brackets (2026-08-28). */}
      <ScheduleEditorModal
        open={editingSchedule !== null}
        schedule={editingSchedule === 'create' ? null : editingSchedule}
        takenCodes={
          editingSchedule === 'create' || editingSchedule === null
            ? scheduleCodes
            : scheduleCodes.filter((code) => code !== editingSchedule.code)
        }
        onCancel={() => setEditingSchedule(null)}
        onSave={saveSchedule}
      />

      {/* S2.1 — group edit: names / policy rate / taxonomy link. The code is
          IMMUTABLE — W6 read-only presentation (text + LockOutlined + tag),
          never a disabled input. */}
      <Modal
        open={!!editingGroup}
        title={
          editingGroup
            ? t('pricing:pricing.groupsAdmin.editModal.title', { code: editingGroup.code })
            : ''
        }
        onCancel={closeGroupEdit}
        width={520}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeGroupEdit}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => void handleGroupSubmit(onGroupEditValid)()}
          >
            {t('common:actions.save')}
          </Button>,
        ]}
      >
        {editingGroup && (
          <div style={{ display: 'grid', gap: token.marginSM }}>
            <div>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.groupsAdmin.editModal.codeLabel')}{' '}
                <LockOutlined style={{ fontSize: token.sizeXS, color: token.colorTextTertiary }} />
              </Text>
              {/* READ-ONLY — products and 折扣卡 reference the code, so it
                  never changes after creation (no input exists for it). */}
              <div>
                <Tag color="purple" style={{ margin: 0 }}>
                  {editingGroup.code}
                </Tag>
              </div>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {t('pricing:pricing.groupsAdmin.editModal.codeImmutable')}
              </Text>
            </div>

            <div>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.quickCreate.nameZh')}
              </Text>
              <Controller
                name="nameZh"
                control={groupControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    status={groupErrors.nameZh ? 'error' : undefined}
                  />
                )}
              />
              {fieldErrorText(groupErrors.nameZh?.message)}
            </div>

            <div>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.quickCreate.nameEn')}
              </Text>
              <Controller
                name="nameEn"
                control={groupControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    size="large"
                    style={formStyles.inputStyle}
                    status={groupErrors.nameEn ? 'error' : undefined}
                  />
                )}
              />
              {fieldErrorText(groupErrors.nameEn?.message)}
            </div>

            <div>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.quickCreate.defaultDiscount')}
              </Text>
              <Controller
                name="defaultDiscountPct"
                control={groupControl}
                render={({ field }) => (
                  <InputNumber
                    value={field.value}
                    onChange={(value) => field.onChange(value ?? null)}
                    onBlur={field.onBlur}
                    min={0}
                    max={100}
                    size="large"
                    addonAfter="%"
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    status={groupErrors.defaultDiscountPct ? 'error' : undefined}
                    aria-label={t('pricing:pricing.quickCreate.defaultDiscount')}
                  />
                )}
              />
              {fieldErrorText(groupErrors.defaultDiscountPct?.message)}
              <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                {t('pricing:pricing.quickCreate.defaultDiscountHint')}
              </Text>
              {(watchedGroupDiscount ?? 0) >= 100 && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('pricing:pricing.warnings.discountZeroPrice')}
                  style={{ marginTop: token.marginXS }}
                />
              )}
            </div>

            <div>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.quickCreate.category')}
              </Text>
              <Controller
                name="categoryId"
                control={groupControl}
                render={({ field }) => (
                  <TaxonomyCascader
                    value={field.value ?? null}
                    onChange={(categoryId) => field.onChange(categoryId)}
                    texts={{
                      placeholder: (label) =>
                        t('pricing:pricing.quickCreate.categoryLevel', { label }),
                      breadcrumbLabel: t('pricing:pricing.quickCreate.categoryBreadcrumb'),
                    }}
                  />
                )}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* The ONE reason gate for S2.1 — group edit save AND activate toggle. */}
      <ReasonRequiredModal
        open={pendingGroupAction !== null}
        title={t('pricing:pricing.groupsAdmin.reason.title')}
        description={t('pricing:pricing.groupsAdmin.reason.description')}
        reasonLabel={t('pricing:pricing.groupsAdmin.reason.label')}
        reasonPlaceholder={t('pricing:pricing.groupsAdmin.reason.placeholder')}
        requiredMessage={t('pricing:pricing.groupsAdmin.reason.required')}
        submitText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        onSubmit={onGroupReasonSubmit}
        onCancel={() => setPendingGroupAction(null)}
      />

      {/* P2 — price band create/edit. The band SET and the CURRENCY are
          chosen at create and are READ-ONLY afterwards (the immutable-code
          presentation: text ＋ LockOutlined, never a disabled input), mirrored
          at type level by `PriceGroupBandPatch` having no member for either.
          Cuts are the only editable thing, and the cover rule is enforced by
          the store, not restated here. */}
      <Modal
        open={editingBand !== null}
        title={
          editingBand === 'create'
            ? t('pricing:pricing.bandsAdmin.editModal.createTitle')
            : editingBand
              ? t('pricing:pricing.bandsAdmin.editModal.editTitle', { group: editingBand.group })
              : ''
        }
        onCancel={closeBandEditor}
        width={560}
        footer={[
          <Button
            key="cancel"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={closeBandEditor}
          >
            {t('common:actions.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={() => void handleBandSubmit(onBandEditValid)()}
          >
            {t('common:actions.save')}
          </Button>,
        ]}
      >
        {editingBand !== null && (
          <div style={{ display: 'grid', gap: token.marginSM }}>
            {bandViolationAlert}

            {/* P3 — a shared cut is ONE number written on two rows, so moving
                it moves the neighbour too. Named here, with both numbers,
                BEFORE the reason gate: disclosed decision, not silent repair. */}
            {bandCutPreview.length > 0 && (
              <Alert
                type="info"
                showIcon
                message={t('pricing:pricing.bandsAdmin.cutMove.title')}
                description={
                  <div style={{ display: 'grid', gap: token.marginXXS }}>
                    {bandCutPreview.map((move) => (
                      <span key={move.bandId} style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {t('pricing:pricing.bandsAdmin.cutMove.line', {
                          group: move.group,
                          bound: t(BAND_CUT_BOUND_KEY[move.field]),
                          from: move.from,
                          to: move.to,
                        })}
                      </span>
                    ))}
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      {t('pricing:pricing.bandsAdmin.cutMove.hint')}
                    </Text>
                  </div>
                }
              />
            )}

            <div>
              <Text style={formStyles.labelStyle}>
                {t('pricing:pricing.bandsAdmin.editModal.scopeLabel')}
                {editingBand !== 'create' && (
                  <>
                    {' '}
                    <LockOutlined
                      style={{ fontSize: token.sizeXS, color: token.colorTextTertiary }}
                    />
                  </>
                )}
              </Text>
              {editingBand === 'create' ? (
                <Controller
                  name="scope"
                  control={bandControl}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      onBlur={field.onBlur}
                      size="large"
                      options={bandScopeCreateOptions}
                      style={{ width: '100%' }}
                      showSearch
                      optionFilterProp="label"
                      aria-label={t('pricing:pricing.bandsAdmin.editModal.scopeLabel')}
                    />
                  )}
                />
              ) : (
                <div>
                  <Tag color="purple" style={{ margin: 0 }}>
                    {bandSetLabel(bandSetScopeOf(editingBand))}
                  </Tag>
                </div>
              )}
              <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                {editingBand === 'create'
                  ? t('pricing:pricing.bandsAdmin.setHint')
                  : t('pricing:pricing.bandsAdmin.editModal.scopeImmutable')}
              </Text>
            </div>

            <div>
              <Text style={formStyles.labelStyle}>
                {editingBand === 'create' && (
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                )}
                {t('pricing:pricing.bandsAdmin.editModal.currencyLabel')}
                {editingBand !== 'create' && (
                  <>
                    {' '}
                    <LockOutlined
                      style={{ fontSize: token.sizeXS, color: token.colorTextTertiary }}
                    />
                  </>
                )}
              </Text>
              {editingBand === 'create' ? (
                <>
                  <Controller
                    name="currency"
                    control={bandControl}
                    render={({ field }) => (
                      <Select
                        value={field.value || undefined}
                        onChange={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        size="large"
                        options={currencySelectOptions}
                        style={{ width: '100%' }}
                        showSearch
                        optionFilterProp="label"
                        status={bandErrors.currency ? 'error' : undefined}
                        aria-label={t('pricing:pricing.bandsAdmin.editModal.currencyLabel')}
                      />
                    )}
                  />
                  {fieldErrorText(bandErrors.currency?.message)}
                </>
              ) : (
                <>
                  <div>
                    <Tag style={{ margin: 0 }}>{editingBand.currency}</Tag>
                  </div>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                    {t('pricing:pricing.bandsAdmin.editModal.currencyImmutable')}
                  </Text>
                </>
              )}
            </div>

            <div>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.bandsAdmin.editModal.groupLabel')}
              </Text>
              <Controller
                name="group"
                control={bandControl}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    onBlur={field.onBlur}
                    size="large"
                    options={PRICE_GROUP_CODES.map((code) => ({
                      label: t('pricing:pricing.matrix.groupColumn', { group: code }),
                      value: code,
                    }))}
                    style={{ width: '100%' }}
                    aria-label={t('pricing:pricing.bandsAdmin.editModal.groupLabel')}
                  />
                )}
              />
            </div>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                  {t('pricing:pricing.bandsAdmin.editModal.minLabel')}
                </Text>
                <Controller
                  name="minInclusive"
                  control={bandControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 0)}
                      onBlur={field.onBlur}
                      min={0}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      status={bandErrors.minInclusive ? 'error' : undefined}
                      aria-label={t('pricing:pricing.bandsAdmin.editModal.minLabel')}
                    />
                  )}
                />
                {fieldErrorText(bandErrors.minInclusive?.message)}
              </Col>
              <Col span={12}>
                <Text style={formStyles.labelStyle}>
                  {t('pricing:pricing.bandsAdmin.editModal.maxLabel')}
                </Text>
                <Controller
                  name="maxExclusive"
                  control={bandControl}
                  render={({ field }) => (
                    <InputNumber
                      value={field.value}
                      // Blank IS the open-ended top band — never coerced to a
                      // fabricated ceiling (the schedules card's open-period
                      // precedent: an absent side is an OPEN end).
                      onChange={(value) => field.onChange(value ?? null)}
                      onBlur={field.onBlur}
                      size="large"
                      style={{ ...formStyles.inputStyle, width: '100%' }}
                      placeholder={t('pricing:pricing.bandsAdmin.editModal.openEnded')}
                      status={bandErrors.maxExclusive ? 'error' : undefined}
                      aria-label={t('pricing:pricing.bandsAdmin.editModal.maxLabel')}
                    />
                  )}
                />
                {fieldErrorText(bandErrors.maxExclusive?.message)}
              </Col>
            </Row>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.bandsAdmin.editModal.maxHint')}
            </Text>
          </div>
        )}
      </Modal>

      {/* The ONE reason gate for P2 — band create, edit AND activate toggle.
          Band changes ARE price-affecting (a band chooses the markup), so
          there is no unreasoned band write anywhere. */}
      <ReasonRequiredModal
        open={pendingBandAction !== null}
        title={t('pricing:pricing.bandsAdmin.reason.title')}
        description={t('pricing:pricing.bandsAdmin.reason.description')}
        reasonLabel={t('pricing:pricing.bandsAdmin.reason.label')}
        reasonPlaceholder={t('pricing:pricing.bandsAdmin.reason.placeholder')}
        requiredMessage={t('pricing:pricing.bandsAdmin.reason.required')}
        submitText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        onSubmit={onBandReasonSubmit}
        onCancel={() => setPendingBandAction(null)}
      />

      {/* S2.3 — 折扣卡 add/edit (its own file, ScheduleEditorModal precedent). */}
      <GroupDiscountCardModal
        open={editingCard !== null}
        card={editingCard === 'create' ? null : editingCard}
        groups={pricingGroups}
        levels={activeLevels}
        existingCards={discountCards}
        onCancel={() => setEditingCard(null)}
        onSave={saveDiscountCard}
      />
    </>
  );
};

export default GroupMatrixTab;
